import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import DialogModal from '../components/DialogModal';
import {
  PageHeader,
  FilterBar,
  DataTable,
  Modal,
  FormInput,
  FormSelect,
  StatusBadge
} from '../components/finance';
import { CircleNotch } from '@phosphor-icons/react';
import './ExpensesDetailPage.css';

// Helper function to safely extract status string value
const getStatusValue = (status) => {
  if (!status) return 'draft';
  if (typeof status === 'string') return status;
  if (typeof status === 'object' && status.value) return status.value;
  return String(status).toLowerCase();
};

const ExpensesDetailPage = ({ autoOpenCreate = false }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNewExpenseModal, setShowNewExpenseModal] = useState(autoOpenCreate);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    amount: '',
    category: 'utilities',
    description: '',
    vendor_name: '',
    expense_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [editFormData, setEditFormData] = useState({
    amount: '',
    category: '',
    description: '',
    vendor_name: '',
    expense_date: '',
    notes: '',
  });

  // Prompt dialog (replaces window.prompt for rejection reason)
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  // Dialog Modal
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });

  // Dialog helpers
  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  const expenseCategories = [
    'utilities',
    'supplies',
    'travel',
    'maintenance',
    'professional_services',
  ].map(cat => ({ label: cat.toUpperCase(), value: cat }));

  const statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Draft', value: 'draft' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Posted', value: 'posted' }
  ];

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/expenses');
      setExpenses(Array.isArray(data) ? data : data.expenses || []);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/api/expenses', formData);
      toast.success('Expense created successfully');
      setShowNewExpenseModal(false);
      setFormData({
        amount: '',
        category: 'utilities',
        description: '',
        vendor_name: '',
        expense_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      fetchExpenses();
    } catch (error) {
      toast.error(error.message || 'Failed to create expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitExpense = async (expenseId) => {
    try {
      await api.post(`/api/expenses/${expenseId}/submit`, {});
      toast.success('Expense submitted for approval');
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      showDialog({
        title: 'Submission Failed',
        message: error.message || 'Failed to submit expense for approval. Please try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    }
  };

  const handleApproveExpense = async (expenseId, reason = '') => {
    try {
      await api.post(`/api/expenses/${expenseId}/approve`, { approval_notes: reason });
      toast.success('Expense approved');
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      showDialog({
        title: 'Approval Failed',
        message: error.message || 'Failed to approve this expense. Please try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    }
  };

  const handleRejectExpense = async (expenseId, reason) => {
    try {
      await api.post(`/api/expenses/${expenseId}/reject`, { rejection_reason: reason });
      toast.success('Expense rejected');
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      showDialog({
        title: 'Rejection Failed',
        message: error.message || 'Failed to reject this expense. Please try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    }
  };

  const handlePostExpense = async (expenseId) => {
    try {
      await api.post(`/api/expenses/${expenseId}/post`, {});
      toast.success('Expense posted to GL');
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      showDialog({
        title: 'Posting Failed',
        message: error.message || 'Failed to post this expense to the General Ledger. Please try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditClick = (expense) => {
    setEditingExpense(expense);
    setEditFormData({
      amount: expense.amount,
      category: expense.category,
      description: expense.description,
      vendor_name: expense.vendor_name || '',
      expense_date: expense.expense_date.split('T')[0],
      notes: expense.notes || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    if (!editingExpense) return;
    try {
      await api.put(`/api/expenses/${editingExpense.id}`, editFormData);
      toast.success('Expense updated successfully');
      setShowEditModal(false);
      setEditingExpense(null);
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      toast.error(error.message || 'Failed to update expense');
    }
  };

  const filteredExpenses = filterStatus === 'all'
    ? expenses
    : expenses.filter(exp => getStatusValue(exp.status).toLowerCase() === filterStatus.toLowerCase());

  // DataTable columns configuration
  const columns = [
    {
      key: 'expense_date',
      label: 'Date',
      render: (value) => new Date(value).toLocaleDateString()
    },
    { key: 'description', label: 'Description' },
    { key: 'vendor_name', label: 'Vendor' },
    { key: 'category', label: 'Category' },
    {
      key: 'amount',
      label: 'Amount',
      render: (value) => `GHS ${value.toLocaleString()}`
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => <StatusBadge status={getStatusValue(value)} />
    }
  ];

  // DataTable actions configuration
  const tableActions = [
    {
      label: 'View',
      className: 'primary',
      onClick: (row) => setSelectedExpense(row)
    },
    {
      label: 'Submit',
      className: 'success',
      onClick: (row) => {
        if (getStatusValue(row.status).toLowerCase() === 'draft') {
          handleSubmitExpense(row.id);
        }
      }
    }
  ];

  // FilterBar configuration
  const filterConfig = [
    {
      name: 'status',
      label: 'Filter by Status',
      type: 'select',
      value: filterStatus,
      options: statusOptions
    }
  ];

  return (
    <div className="expenses-detail-page">
      <PageHeader
        title="Expense Management"
        subtitle="Create, review, and post expenses"
        actions={[
          {
            label: 'New Expense',
            className: 'primary',
            onClick: () => setShowNewExpenseModal(true)
          }
        ]}
      />

      <FilterBar
        filters={filterConfig}
        onChange={(name, value) => setFilterStatus(value)}
        onReset={() => setFilterStatus('all')}
      />

      <DataTable
        columns={columns}
        data={filteredExpenses}
        actions={tableActions}
        loading={loading}
        emptyMessage="No expenses found"
      />

      <Modal
        isOpen={showNewExpenseModal}
        onClose={() => {
          setShowNewExpenseModal(false);
          setFormData({
            amount: '',
            category: 'utilities',
            description: '',
            vendor_name: '',
            expense_date: new Date().toISOString().split('T')[0],
            payment_method: 'bank_transfer',
            reference_number: '',
            notes: '',
          });
        }}
        title="Create New Expense"
        actions={[
          {
            label: submitting ? 'Creating...' : 'Create Expense',
            className: 'primary',
            onClick: handleCreateExpense,
            isLoading: submitting
          },
          {
            label: 'Cancel',
            className: 'secondary',
            onClick: () => setShowNewExpenseModal(false),
            disabled: submitting
          }
        ]}
      >
        <form onSubmit={handleCreateExpense}>
          <FormInput
            label="Expense Date"
            name="expense_date"
            type="date"
            value={formData.expense_date}
            onChange={handleFormChange}
            disabled={submitting}
            required
          />

          <FormInput
            label="Amount (GHS)"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleFormChange}
            placeholder="0.00"
            disabled={submitting}
            step={0.01}
            required
          />

          <FormSelect
            label="Category"
            name="category"
            value={formData.category}
            onChange={handleFormChange}
            options={expenseCategories}
            disabled={submitting}
            required
          />

          <FormInput
            label="Description"
            name="description"
            type="text"
            value={formData.description}
            onChange={handleFormChange}
            placeholder="e.g., Monthly office supplies"
            disabled={submitting}
            required
          />

          <FormInput
            label="Vendor Name"
            name="vendor_name"
            type="text"
            value={formData.vendor_name}
            onChange={handleFormChange}
            placeholder="e.g., ABC Supplies Ltd"
            disabled={submitting}
          />

          <FormInput
            label="Notes"
            name="notes"
            type="textarea"
            value={formData.notes}
            onChange={handleFormChange}
            placeholder="Additional notes"
            disabled={submitting}
            rows={3}
          />
        </form>
      </Modal>

      <Modal
        isOpen={!!selectedExpense}
        onClose={() => setSelectedExpense(null)}
        title="Expense Details"
        actions={[
          ...(getStatusValue(selectedExpense?.status).toLowerCase() === 'draft' ? [{
            label: 'Edit',
            className: 'secondary',
            onClick: () => handleEditClick(selectedExpense)
          }, {
            label: 'Submit for Approval',
            className: 'primary',
            onClick: () => handleSubmitExpense(selectedExpense.id)
          }] : []),
          ...(getStatusValue(selectedExpense?.status).toLowerCase() === 'pending' ? [{
            label: 'Edit',
            className: 'secondary',
            onClick: () => handleEditClick(selectedExpense)
          }, {
            label: 'Approve',
            className: 'success',
            onClick: () => handleApproveExpense(selectedExpense.id)
          }, {
            label: 'Reject',
            className: 'danger',
            onClick: () => {
              const expenseId = selectedExpense.id;
              setPromptDialog({
                title: 'Reject Expense',
                message: 'Enter rejection reason:',
                defaultValue: '',
                onSubmit: (value) => {
                  if (value) handleRejectExpense(expenseId, value);
                },
              });
            }
          }] : []),
          ...(getStatusValue(selectedExpense?.status).toLowerCase() === 'approved' ? [{
            label: 'Post to GL',
            className: 'primary',
            onClick: () => handlePostExpense(selectedExpense.id)
          }] : []),
          {
            label: 'Close',
            className: 'secondary',
            onClick: () => setSelectedExpense(null)
          }
        ]}
      >
        {selectedExpense && (
          <div className="expense-details">
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '16px'
            }}>
              <div>
                <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Amount</label>
                <p style={{ fontSize: '16px', fontWeight: 600, color: '#0066cc' }}>
                  GHS {selectedExpense.amount.toLocaleString()}
                </p>
              </div>
              <div>
                <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Category</label>
                <p>{selectedExpense.category}</p>
              </div>
              <div>
                <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Status</label>
                <p><StatusBadge status={getStatusValue(selectedExpense.status)} /></p>
              </div>
              <div>
                <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Date</label>
                <p>{new Date(selectedExpense.expense_date).toLocaleDateString()}</p>
              </div>
              <div>
                <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Vendor</label>
                <p>{selectedExpense.vendor_name || '-'}</p>
              </div>
              <div>
                <label style={{ fontWeight: 600, color: '#666', fontSize: '12px' }}>Reference</label>
                <p>{selectedExpense.reference_number || '-'}</p>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: 600, color: '#666', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Description
              </label>
              <p>{selectedExpense.description}</p>
            </div>

            {selectedExpense.notes && (
              <div>
                <label style={{ fontWeight: 600, color: '#666', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  Notes
                </label>
                <p>{selectedExpense.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingExpense(null);
        }}
        title="Edit Expense"
        actions={[
          {
            label: 'Update Expense',
            className: 'primary',
            onClick: handleUpdateExpense
          },
          {
            label: 'Cancel',
            className: 'secondary',
            onClick: () => {
              setShowEditModal(false);
              setEditingExpense(null);
            }
          }
        ]}
      >
        <form onSubmit={handleUpdateExpense}>
          <FormInput
            label="Expense Date"
            name="expense_date"
            type="date"
            value={editFormData.expense_date}
            onChange={handleEditFormChange}
            required
          />

          <FormInput
            label="Amount (GHS)"
            name="amount"
            type="number"
            value={editFormData.amount}
            onChange={handleEditFormChange}
            placeholder="0.00"
            step={0.01}
            required
          />

          <FormSelect
            label="Category"
            name="category"
            value={editFormData.category}
            onChange={handleEditFormChange}
            options={expenseCategories}
            required
          />

          <FormInput
            label="Description"
            name="description"
            type="text"
            value={editFormData.description}
            onChange={handleEditFormChange}
            placeholder="e.g., Monthly office supplies"
            required
          />

          <FormInput
            label="Vendor Name"
            name="vendor_name"
            type="text"
            value={editFormData.vendor_name}
            onChange={handleEditFormChange}
            placeholder="e.g., ABC Supplies Ltd"
          />

          <FormInput
            label="Notes"
            name="notes"
            type="textarea"
            value={editFormData.notes}
            onChange={handleEditFormChange}
            placeholder="Additional notes"
            rows={3}
          />
        </form>
      </Modal>

      {/* Dialog Modal */}
      <DialogModal
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        primaryAction={dialogConfig.primaryAction}
        secondaryAction={dialogConfig.secondaryAction}
      />

      {/* Prompt Dialog (rejection reason) */}
      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="warning"
        input={{ placeholder: 'Reason for rejection', defaultValue: promptDialog?.defaultValue || '', required: true }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
};

export default ExpensesDetailPage;


